import React, { Component } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonFooter,
  IonInput,
  IonButton,
  IonIcon,
  IonSpinner,
} from '@ionic/react';
import { sendOutline, trashOutline } from 'ionicons/icons';

import ChatService from '../services/chat';
import ProfileService from '../services/profile';
import { getEcho, disconnectEcho } from '../services/echo';

import '../theme/chat.css';

class ChatPage extends Component {
  constructor(props) {
    super(props);
    this.messagesEndRef = React.createRef();
    this.inputRef = React.createRef();
    this.state = {
      messages: [],
      newMessage: '',
      loading: true,
      sending: false,
      unreadCount: 0,
      userName: '',
      deleteMsgId: null,
    };
  }

  componentDidMount() {
    this._isMounted = true;
    this.loadProfile();
    this.loadMessages();
    // this.setupPolling(); // non voglio polling solo websocket
  }

  componentWillUnmount() {
    this._isMounted = false;
    disconnectEcho();
  }

  setupPolling() {
    this._pollingInterval = setInterval(() => {
      if (this._isMounted) {
        this.loadMessages(1, true);
      }
    }, 15000);
  }

  setupEcho(userId) {
    if (this._echoReady) return;
    try {
      const echo = getEcho();

      const channel = echo.private(`chat.user.${userId}`);
      channel.subscribed(() => {
        this._echoReady = true;
      });

      channel.error((err) => {
        console.error(`[Echo] subscription error for private-chat.user.${userId}:`, err);
      });

      echo.connector.pusher.connection.bind('connected', () => {});
      echo.connector.pusher.connection.bind('disconnected', () => {});
      echo.connector.pusher.connection.bind('error', (err) => {
        console.error('[Echo] Pusher error:', err);
      });

      channel.listen('.Chat.ChatUserEvent', (event) => {
        if (this._isMounted) {
          this.setState((prevState) => {
            if (prevState.messages.some((m) => m.id === event.id)) {
              return null;
            }

            return { messages: [...prevState.messages, event] };
          });

          this.scrollToBottom();
          setTimeout(() => {
            if (this.inputRef.current) {
              this.inputRef.current.setFocus();
            }
          }, 100);
        }
      });

      channel.listen('.Chat.RefreshChatUserEvent', () => {
        if (this._isMounted) this.loadMessages();
      });
    } catch (e) {
      console.error('[Echo] setup failed:', e);
    }
  }

  loadProfile = async () => {
    try {
      const response = await ProfileService.me();
      if (response && response.data && response.data.data) {
        const userData = response.data.data;
        this.setState({ userName: userData.name || '' });

        if (userData.id) {
          this.setupEcho(userData.id);
        } else {
          console.error('[Echo] Profile response missing user id');
        }
      }
    } catch (e) {
      console.error('[Echo] Failed to load profile:', e);
    }
  };

  loadMessages = async (page = 1, isPolling = false) => {
    try {
      const response = await ChatService.get(page);
      const data = response.data.data || response.data;
      const newMessages = Array.isArray(data) ? data.reverse() : (data.data || []).reverse();

      if (this._isMounted) {
        this.setState((prevState) => {
          if (isPolling && prevState.messages.length > 0) {
            const existingIds = new Set(prevState.messages.map((m) => m.id));
            const added = newMessages.filter((m) => !existingIds.has(m.id));
            if (added.length === 0) return null;
            return { messages: [...prevState.messages, ...added] };
          }

          return { messages: newMessages, loading: false };
        });

        this.scrollToBottom();
      }
    } catch (error) {
      console.error('Error loading messages:', error);
      if (this._isMounted) {
        this.setState({ loading: false });
      }
    }
  };

  handleSend = async () => {
    const { newMessage, sending } = this.state;
    if (!newMessage.trim() || sending) return;

    this.setState({ sending: true });
    try {
      const response = await ChatService.send(newMessage.trim());
      const sentMsg = response.data;

      this.setState((prevState) => {
        if (prevState.messages.some((m) => m.id === sentMsg.id)) {
          return { newMessage: '', sending: false };
        }

        return {
          messages: [...prevState.messages, sentMsg],
          newMessage: '',
          sending: false,
        };
      });

      this.scrollToBottom();
    } catch (error) {
      console.error('Error sending message:', error);
      this.setState({ sending: false }, () => {
        setTimeout(() => {
          if (this.inputRef.current) {
            this.inputRef.current.setFocus();
          }
        }, 100);
      });
    }
  };

  handleKeyPress = (e) => {
    if (e.key === 'Enter') this.handleSend();
  };

  handleDelete = async (id) => {
    try {
      await ChatService.delete(id);
      if (this._isMounted) {
        this.setState((prevState) => ({
          messages: prevState.messages.filter((m) => m.id !== id),
          deleteMsgId: null,
        }));
      }
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  scrollToBottom = () => {
    setTimeout(() => {
      if (this.messagesEndRef.current) {
        this.messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  formatTime(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const romeOffset = 120;
    const utcMinutes = d.getUTCHours() * 60 + d.getUTCMinutes();
    const localMinutes = utcMinutes + romeOffset;
    const hours = String(Math.floor(localMinutes / 60) % 24).padStart(2, '0');
    const mins = String(localMinutes % 60).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    const month = String(d.getUTCMonth() + 1).padStart(2, '0');
    const year = d.getUTCFullYear();
    return `${day}/${month}/${year} ${hours}:${mins}`;
  }

  renderSystemToClientMessage(msg, index) {
    return (
      <div key={index} className="message-wrapper message-received message-system">
        <div className="message-avatar avatar-pharmacy">🥼</div>
        <div className="message-bubble bubble-received bubble-system-pharmacy">
          <div className="message-text">{msg.content}</div>
          <div className="message-time">{this.formatTime(msg.created_at)}</div>
        </div>
      </div>
    );
  }

  renderSystemToPharmacyMessage(msg, index) {
    const initials = this.getInitials(this.state.userName);

    return (
      <div key={index} className="message-wrapper message-sent message-system">
        <div className="message-bubble bubble-sent bubble-system-client">
          <div className="message-text">{msg.content}</div>
          <div className="message-time">{this.formatTime(msg.created_at)}</div>
        </div>

        <div className="message-avatar avatar-user">{initials}</div>
      </div>
    );
  }

  getInitials(name) {
    if (!name) return '?';
    return name
      .split(' ')
      .map((s) => s.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('');
  }

  renderSentMessage(msg, index) {
    const initials = this.getInitials(this.state.userName);
    const { deleteMsgId } = this.state;
    const showDelete = deleteMsgId === msg.id;

    return (
      <div key={index} className={`sent-message-wrapper${showDelete ? ' show-delete' : ''}`}>
        <div
          className="remove-btn-overlay"
          onClick={(e) => {
            e.stopPropagation();
            this.handleDelete(msg.id);
          }}
        >
          <IonIcon icon={trashOutline} />
        </div>

        <div
          className="sent-message-content"
          onClick={(e) => {
            e.stopPropagation();
            this.setState({ deleteMsgId: showDelete ? null : msg.id });
          }}
        >
          <div className="message-bubble bubble-sent">
            <div className="message-text">{msg.content}</div>
            <div className="message-time">{this.formatTime(msg.created_at)}</div>
          </div>

          <div className="message-avatar avatar-user">{initials}</div>
        </div>
      </div>
    );
  }

  renderReceivedMessage(msg, index) {
    return (
      <div key={index} className="message-wrapper message-received">
        <div className="message-avatar avatar-pharmacy">🥼</div>
        <div className="message-bubble bubble-received">
          <div className="message-text">{msg.content}</div>
          <div className="message-time">{this.formatTime(msg.created_at)}</div>
        </div>
      </div>
    );
  }

  renderMessage(msg, index) {
    if (msg.type !== 'message') {
      if (msg.direction === 'system_to_client') {
        return this.renderSystemToClientMessage(msg, index);
      }

      return this.renderSystemToPharmacyMessage(msg, index);
    }

    if (msg.direction === 'client_to_pharmacy') {
      return this.renderSentMessage(msg, index);
    }

    return this.renderReceivedMessage(msg, index);
  }

  render() {
    const { messages, newMessage, loading, sending } = this.state;

    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonTitle>Chat</IonTitle>
          </IonToolbar>
        </IonHeader>

        <IonContent className="chat-page" onClick={() => this.setState({ deleteMsgId: null })}>
          {loading ? (
            <div className="chat-loading">
              <IonSpinner />
              <p>Caricamento messaggi...</p>
            </div>
          ) : (
            <div className="messages-container">
              {messages.length === 0 ? (
                <div className="chat-empty">
                  <p>Nessun messaggio. Invia un messaggio per iniziare la conversazione.</p>
                </div>
              ) : (
                messages.map((msg, index) => this.renderMessage(msg, index))
              )}

              <div ref={this.messagesEndRef} />
            </div>
          )}
        </IonContent>

        <IonFooter>
          <div className="chat-input-container">
            <IonInput
              ref={this.inputRef}
              className="chat-input"
              placeholder="Scrivi un messaggio..."
              value={newMessage}
              onIonChange={(e) => this.setState({ newMessage: e.detail.value })}
              onKeyPress={this.handleKeyPress}
              disabled={sending}
              maxlength={5000}
            />

            <IonButton
              className="chat-send-button"
              onClick={this.handleSend}
              disabled={!newMessage.trim() || sending}
              fill="clear"
            >
              <IonIcon icon={sendOutline} />
            </IonButton>
          </div>
        </IonFooter>
      </IonPage>
    );
  }
}

export default ChatPage;
